import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import { closeModal } from "../../redux/reducers/dialog";
import { zIndex } from "../../theme/tokens";
import TextView from "../TextView";
import Button from "../Button";

/** Copy shown under the subtitle when a caller does not supply its own note. */
export const DEFAULT_CONFIRM_NOTE = "This action cannot be undone.";
/** Confirm-button label when a caller does not supply its own. */
export const DEFAULT_CONFIRM_LABEL = "Confirm";
/** Cancel-button label when a caller does not supply its own. */
export const DEFAULT_CANCEL_LABEL = "Cancel";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  // MUI's Dialog defaults to z-index 1300, which is EXACTLY the app's
  // `zIndex.overlay` token -- so any overlay that sits at overlay+1 (the
  // "Captured items on this wheel" drawer is at 1301) would render ON TOP of
  // this confirm dialog and its backdrop, leaving the user with a half-occluded
  // modal they cannot fully read. Pin the shared confirm to the token scale's
  // `modal` layer (1400), which is where it belongs by design: modal outranks
  // overlay.
  zIndex: zIndex.modal,
  ".MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.8)",
  },
  ".MuiPaper-root": {
    // `position: relative` makes the absolutely-pinned close X anchor to THIS
    // paper's corner (its padding box) at every width, not to some further-up
    // positioned ancestor -- so the X stays in the top-right corner regardless
    // of viewport size (QA-1045 corner-X pinning law).
    position: "relative",
    padding: "15px 8px",
    border: "1px solid #E1E1E1",
    borderRadius: 8,
  },
}));

// Horizontal space the pinned close X occupies at the paper's top-right,
// measured from the paper's right padding edge: `CLOSE_X_RIGHT` inset +
// the IconButton's own hit box. The title reserves this much right-padding so
// its text can NEVER run underneath the X at narrow widths.
const CLOSE_X_RIGHT = 8;
const CLOSE_X_HIT = 40; // MUI IconButton: 24px icon + 8px padding each side.
const TITLE_RIGHT_RESERVE = CLOSE_X_RIGHT + CLOSE_X_HIT; // 48px

const DeleteModal = () => {
  const dispatch = useDispatch();
  const {
    isOpen,
    title,
    subtitle,
    note,
    confirmLabel,
    cancelLabel,
    onCancel,
    onConfirm,
  } = useSelector((state) => state.deleteDialog);

  const handleCancel = () => {
    if (onCancel) onCancel();
    dispatch(closeModal());
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    dispatch(closeModal());
  };

  // `note: false` is an explicit suppress (Capture post-create). Omit or
  // "" still keeps the previous, permanent-delete copy.
  const showNote = note !== false;
  const showBody = Boolean(subtitle) || showNote;

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleCancel}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <DialogTitle
        // Reserve the X's corner so the title text wraps BEFORE it, never
        // under it -- at 320 / 375 / 768 / 1024 and every width between.
        sx={{ pr: `${TITLE_RIGHT_RESERVE}px` }}
      >
        <TextView variant="title">
          <WarningIcon color="warning" />{" "}
          <span style={{ marginLeft: "10px" }}>{title}</span>
        </TextView>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            // Pinned to the paper's top-right corner (see `.MuiPaper-root`
            // position:relative above). Fixed inset, never in the flex flow,
            // so it cannot be pushed onto the title. >=40px hit target.
            position: "absolute",
            right: CLOSE_X_RIGHT,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {showBody ? (
        <DialogContent>
          {subtitle ? (
            <TextView variant="subtitle">{subtitle}</TextView>
          ) : null}
          {showNote ? (
            <TextView variant="subtitle">
              {note || DEFAULT_CONFIRM_NOTE}
            </TextView>
          ) : null}
        </DialogContent>
      ) : null}
      <DialogActions>
        <Button
          style={{
            width: "auto",
            minWidth: 120,
            paddingLeft: 16,
            paddingRight: 16,
          }}
          variant={"secondary"}
          onClick={handleCancel}
        >
          {cancelLabel || DEFAULT_CANCEL_LABEL}
        </Button>
        <Button
          onClick={handleConfirm}
          style={{
            width: "auto",
            minWidth: 100,
            paddingLeft: 16,
            paddingRight: 16,
            backgroundColor: "#822B86",
          }}
        >
          {confirmLabel || DEFAULT_CONFIRM_LABEL}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default DeleteModal;
